import React, { useState, useEffect } from 'react';

import i18n from 'i18n-react';
import { baseAppId, storageKeys } from 'settings';
import { APIHandler, StorageHandler, DataHandler, Modal, Diploma } from 'C4';

import badges from 'assets/images/badges';

import BadgeDisplay from 'components/BadgeDisplay';
import ContentHeader from 'components/ContentHeader';
import RewardMenu from 'components/RewardMenu';

import { usePointsHandler } from 'hooks/usePointsHandler';
import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

const params = new URLSearchParams(window.location.search);
let mobile = false;
if (params.get('mobile')) {
  mobile = true;
}

const getTotalPages = (res) => {
  let totalPages = 0;
  if (res && res.data && res.data[0]) {
    const { chapters } = res.data[0];
    // Limit to the first three chapters
    chapters.slice(0, 3).forEach((chapter) => {
      totalPages += chapter.total_pages - 1;
    });
  }
  return totalPages;
};

// Makes sure progress is returned in percentage value
const calculateProgress = (progress, pages) =>
  Math.floor((progress / pages) * 100);

const getTotalProgress = (res) => {
  let totalProgress = 0;
  if (res && res.data && res.data[0]) {
    const { chapters } = res.data[0];
    // Limit to the first three chapters
    chapters.slice(0, 3).forEach((chapter) => {
      totalProgress += chapter.progress;
    });
  }
  return totalProgress;
};

/**
 * BadgeView
 *
 * A view component that displays the diploma and each attained badge for the user. These badges being:
 *
 * - Bronze/Silver/Gold/Platinum badges: Based on the amount of completed trainings
 * - Specialization badges
 * - Partner Badges
 *
 * @returns {JSX.Element} A component that displays the user's badges and progress.
 */
const BadgeView = () => {
  const [chapters, setChapters] = useState([]);
  const [subApps, setSubApps] = useState([]);
  const [totalProgressValue, setTotalProgressValue] = useState(0);
  const [showDiploma, setShowDiploma] = useState(false);
  const [diplomaImage, setDiplomaImage] = useState('');
  const [destinationName, setDestinationName] = useState('');

  const {
    appId,
    lang: { langFile },
    loaded: settingsLoaded,
  } = useSettings();
  const {
    user: { first_name: firstName, last_name: lastName, email },
    loaded: userInfoLoaded,
  } = useUserInfo();
  const {
    tiers,
    completedItems,
    totalPoints,
    getTieredValue,
    loaded: loadedPointsHandler,
  } = usePointsHandler();

  const { tierBadges } = badges;
  const tierIndex = tiers && Math.min(getTieredValue(), tiers.length - 1);
  const currentPath = window.location.pathname;

  const handleDiplomaOpen = () => setShowDiploma(true);
  const handleDiplomaClose = () => setShowDiploma(false);

  useEffect(() => {
    const fetchData = async () => {
      const appInfo = await StorageHandler.load(storageKeys.appInfo);
      if (appInfo) {
        setDestinationName(appInfo.appName);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const image = `https://cms.travpromobile.com/api/app/diploma?first_name=${firstName}&last_name=${lastName}&app_id=${appId}&lang=${langFile}_${langFile
      .replace('pt', 'br')
      .replace('ko', 'kr')
      .toUpperCase()}`;
    setDiplomaImage(image);
  }, [firstName, lastName, appId, langFile]);

  useEffect(() => {
    const getUserInfo = () => {
      StorageHandler.load(storageKeys.user).then((res) => {
        if (res) {
          APIHandler.getUserProgress(appId, email).then((userProgress) => {
            if (userProgress) {
              const totalProgress = getTotalProgress(userProgress);
              const totalPages = getTotalPages(userProgress);
              const percentage = calculateProgress(totalProgress, totalPages);
              setTotalProgressValue(percentage >= 100 ? 100 : percentage);
            }
          });
        } else {
          setTotalProgressValue(0);
        }
      });
    };

    const getSubAppData = (appIds) => {
      if (appIds.apps.length > 0) {
        return APIHandler.getUserProgress(appIds.apps, email).then(
          (userProgress) => {
            if (userProgress) {
              const progress = Object.values(userProgress.data).map((app) => {
                const [chapter] = app.chapters;
                const { appIcon, name, badge } = app;
                return {
                  chapter,
                  name,
                  appIcon,
                  badge,
                };
              });
              return progress;
            }
          }
        );
      }
    };

    const getSubApps = async () => {
      const ids = await APIHandler.getSubApps(baseAppId);
      if (ids) {
        const data = await getSubAppData(ids);
        if (data) {
          setSubApps(data);
        }
      }
    };

    const getChaptersAndProgress = () => {
      APIHandler.getChaptersAndProgress(appId, email).then((data) => {
        setChapters(data.chapters.uncategorized[0].chapters);
      });
    };

    if (settingsLoaded && userInfoLoaded) {
      getChaptersAndProgress();
      getSubApps();
      getUserInfo();
    }
  }, [settingsLoaded, userInfoLoaded]);

  return (
    <div className="contentContainer badgeView">
      {currentPath === '/main/rewards/achievements' && <RewardMenu />}
      <ContentHeader
        header={i18n.translate('badgeView.title')}
        subtitle={i18n.translate('badgeView.subtitle', {
          program_name_short: destinationName,
        })}
      />

      <hr />

      <div className="content-box">
        {loadedPointsHandler &&
          tiers && ( // Check if the data has been loaded and tiers are available
            <div className="badgeContainer points__container">
              <ContentHeader
                header={i18n.translate('badgeView.badgeDisplayTitle')}
              />
              <BadgeDisplay
                tierBadges={tierBadges}
                tiers={tiers}
                tierIndex={tierIndex}
                totalPoints={totalPoints}
                layout="longBar"
                hideBadge
                completedItems={completedItems}
                hasPopout
              />
              <div className="badgeView__title--tiers">
                {tiers.map((tier, index) => (
                  <div
                    key={tier.title}
                    className={`tier-title ${
                      tierIndex === index ? 'active' : ''
                    } ${tier.completedItems >= tier.count ? '' : 'disabled'}`}
                  >
                    <img
                      className="badge"
                      src={badges.tierBadges[index]}
                      alt="Logo"
                    />

                    <span className="title">
                      {Math.min(tier.completedItems, tier.count)}
                      {' / '}
                      {tier.count} {i18n.translate('badgeView.complete')}
                    </span>
                  </div>
                ))}
              </div>
              {/* <div className="badge-display__wrapper">
                <h1 className="badgeView__text">{tiers[tierIndex].copy}</h1>
              </div> */}
            </div>
          )}

        {totalProgressValue >= 100 && (
          <div>
            <hr />
            {/* <ContentHeader header={i18n.translate("badgeView.diploma")} /> */}
            <div className="diploma__container" onClick={handleDiplomaOpen}>
              <img
                className="diploma__image"
                src={diplomaImage}
                alt="diploma"
              />
            </div>
            <Modal
              show={showDiploma}
              onHide={handleDiplomaClose}
              bgClose
              autosize
            >
              <Diploma
                appId={appId}
                firstName={firstName}
                lastName={lastName}
                email={email}
                key={firstName}
              />
            </Modal>
          </div>
        )}
        {chapters.filter(
          (chapter) => chapter.percentage === 100 && chapter.badge !== null
        ).length > 0 && (
          <div>
            <hr />
            <ContentHeader header={i18n.translate('badgeView.chapterBadges')} />
            <div className="badges__collection">
              {chapters.map((chapter) => {
                const { id, percentage, badge } = chapter;
                if (percentage === 100 && badge) {
                  return (
                    <a
                      key={id}
                      href={`https://stack.travpromobile.com/download.php?url=${badge}`}
                    >
                      <button
                        className="chapter-badge"
                        style={{
                          backgroundImage: `url(${`${badge.replace(
                            'https://s3.amazonaws.com/developertool',
                            'https://travprocdn.imgix.net/'
                          )}?w=450&fit=clamp`})`,
                        }}
                      />
                    </a>
                  );
                }
              })}
            </div>
          </div>
        )}
        {subApps.filter(
          (subapp) =>
            subapp.chapter.progress >= subapp.chapter.total_pages - 1 &&
            subapp.badge !== null
        ).length > 0 && (
          <div>
            <hr />
            <ContentHeader header={i18n.translate('badgeView.partnerBadges')} />
            <div className="badges__collection">
              {subApps.map((subapp) => {
                const {
                  chapter: { progress, total_pages: totalPages },
                  badge,
                } = subapp;
                if (progress >= totalPages - 1 && badge) {
                  return (
                    <a
                      href={`https://stack.travpromobile.com/download.php?url=${badge}`}
                    >
                      <button
                        className="subapp-badge"
                        style={{
                          backgroundImage: `url(${`${badge.replace(
                            'https://s3.amazonaws.com/developertool',
                            'https://travprocdn.imgix.net/'
                          )}?w=450&fit=clamp`})`,
                        }}
                      />
                    </a>
                  );
                }
              })}
            </div>
          </div>
        )}
      </div>
      {mobile && (
        <style>
          {
            '\
      .buttons__diploma { display: none !important; } .sharebox__buttons { display: none !important; } .contentContainer { margin-top: 5rem; } .header { display: none; } .cookieConsent { display: none; }'
          }
        </style>
      )}
    </div>
  );
};

export default BadgeView;
